import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MapPin, Clock, Phone, Navigation, Stethoscope, AlertTriangle } from 'lucide-react'
import SEOHead from '@/components/SEO/SEOHead'
import PageHeader from '@/components/Common/PageHeader'
import { neighborhoods, coveredPopulation } from '@/data/neighborhoods'
import { contactInfo } from '@/data/contact'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'
import {
  buildGraph,
  breadcrumbListData,
  webPageSchema,
  faqData,
  itemListSchema
} from '@/data/structured-data'

const KarakopruHub: React.FC = () => {
  const telHref = toTelHref(contactInfo.phone)
  const waHref = toWhatsAppHref(contactInfo.whatsapp)

  // Mesafeye göre sıralanır; ziyaretçi için en anlamlı sıra budur.
  const byDistance = [...neighborhoods].sort((a, b) => a.distanceKm - b.distanceKm)
  const nearest = byDistance[0]
  const farthest = byDistance[byDistance.length - 1]

  // Meta açıklama 120-160 karakter aralığında tutulur; Google bu sınırda kesiyor.
  const description =
    `Karaköprü'nün ${neighborhoods.length} mahallesine hizmet veren veteriner kliniği. ` +
    `En uzak mahalleden ${farthest.driveMinutes} dakika. Kedi-köpek muayene, aşı, kısırlaştırma ve 7/24 acil.`

  return (
    <>
      <SEOHead
        title="Karaköprü Veteriner Kliniği"
        description={description}
        keywords="karaköprü veteriner, karakopru veteriner kliniği, şanlıurfa karaköprü veteriner, seyrantepe veteriner, doğukent veteriner, atakent veteriner, akpıyar veteriner, karaköprü kedi köpek veteriner, karaköprü acil veteriner"
        canonicalUrl="/karakopru-veteriner"
        structuredData={buildGraph(
          webPageSchema({
            name: 'Karaköprü Veteriner Kliniği',
            description,
            url: '/karakopru-veteriner'
          }),
          itemListSchema({
            name: 'Hizmet verilen Karaköprü mahalleleri',
            url: '/karakopru-veteriner',
            items: byDistance.map(n => ({
              name: `${n.name} Mahallesi`,
              url: `/karakopru-veteriner/${n.slug}`
            }))
          }),
          faqData([
            {
              question: 'Karaköprü’nün hangi mahallelerine hizmet veriyorsunuz?',
              answer:
                `Karaköprü'nün yerleşik kent dokusunu oluşturan ${neighborhoods.length} mahallenin tamamına hizmet veriyoruz: ` +
                byDistance.map(n => n.name).join(', ') + '. ' +
                `Kliniğimiz Batıkent Mahallesi'nde, Fuar Alanı Kavşağı'nda bulunuyor.`
            },
            {
              question: 'Kliniğe en yakın mahalle hangisi?',
              answer:
                `${nearest.name} Mahallesi ${nearest.distanceKm} km mesafede, araçla yaklaşık ${nearest.driveMinutes} dakika. ` +
                `En uzak noktamız olan ${farthest.name} Mahallesi'nden ise ${farthest.distanceKm} km, yaklaşık ${farthest.driveMinutes} dakika sürüyor.`
            },
            {
              question: 'Acil bir durumda gece gelebilir miyim?',
              answer:
                'Evet. 7/24 acil hizmet veriyoruz. Yolda vakit kaybetmemek için gelmeden önce ' +
                `${contactInfo.phone} numarasını arayın; durumu öğrenip siz gelene kadar hazırlık yapalım.`
            },
            {
              question: 'Randevu almadan gelebilir miyim?',
              answer:
                'Acil vakalarda randevusuz gelebilirsiniz. Rutin muayene, aşı ve kısırlaştırma gibi planlı işlemlerde ' +
                'bekleme süresini kısaltmak için telefonla randevu almanızı öneririz.'
            }
          ]),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'Karaköprü Veteriner', url: '/karakopru-veteriner' }
          ])
        )}
      />

      <PageHeader
        title="Karaköprü Veteriner Kliniği"
        subtitle={`Batıkent'teki kliniğimizden Karaköprü'nün ${neighborhoods.length} mahallesine hizmet veriyoruz. En uzak mahalleden ulaşım süresi ${farthest.driveMinutes} dakika.`}
      />

      {/* Özet kartları */}
      <section className="py-8 bg-white border-b">
        <div className="container-custom grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: MapPin, label: `${neighborhoods.length} mahalle`, sub: 'Karaköprü genelinde' },
            { icon: Navigation, label: `${nearest.distanceKm}–${farthest.distanceKm} km`, sub: 'Klinik mesafesi aralığı' },
            { icon: Clock, label: `${farthest.driveMinutes} dk`, sub: 'En uzak mahalleden' },
            { icon: AlertTriangle, label: '7/24', sub: 'Acil hizmet' }
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="text-center p-4 rounded-xl bg-gradient-to-br from-primary-50 to-accent-50 border border-primary-100"
            >
              <s.icon className="w-5 h-5 text-primary-600 mx-auto mb-2" />
              <div className="font-bold text-gray-900">{s.label}</div>
              <div className="text-xs text-gray-600 mt-1">{s.sub}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="text-2xl font-bold mb-3">Karaköprü’de veteriner ararken</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                Karaköprü, Şanlıurfa’nın en hızlı büyüyen ilçesi ve evcil hayvan sahipliği de aynı hızla artıyor.
                Kliniğimiz {contactInfo.address.split(',')[1]?.trim() || 'Batıkent Mahallesi'}’nde, Fuar Alanı
                Kavşağı’nda bulunuyor. Bu konum Karaköprü’nün merkezine yakın olduğu için, ilçenin büyük
                bölümünden 5 dakikanın altında ulaşılabiliyor.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Aşağıdaki tabloda her mahalleden kliniğe olan gerçek araç mesafesini ve tahmini süreyi
                bulabilirsiniz. Bu değerler tahmin değil, güncel yol ağı üzerinden hesaplanmıştır.
                Acil bir durumda yola çıkmadan önce arayın; hazırlığa hemen başlayalım.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={telHref} className="btn-primary inline-flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {contactInfo.phone}
                </a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                  WhatsApp’tan yazın
                </a>
              </div>
            </div>

            {/* Mahalle tablosu */}
            <div>
              <h2 className="text-2xl font-bold mb-4">Mahalleden kliniğe mesafe</h2>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Mahalle</th>
                      <th className="text-right font-semibold px-4 py-3">Mesafe</th>
                      <th className="text-right font-semibold px-4 py-3">Süre</th>
                      <th className="text-right font-semibold px-4 py-3 hidden sm:table-cell">Nüfus</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {byDistance.map(n => (
                      <tr key={n.slug} className="hover:bg-primary-50/40 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/karakopru-veteriner/${n.slug}`}
                            className="font-medium text-primary-700 hover:text-primary-900 hover:underline"
                          >
                            {n.name} Mahallesi
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">{n.distanceKm} km</td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">{n.driveMinutes} dk</td>
                        <td className="px-4 py-3 text-right text-gray-500 hidden sm:table-cell">
                          {n.population.toLocaleString('tr-TR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Mesafe ve süreler güncel yol ağı üzerinden hesaplanmıştır; trafik yoğunluğuna göre değişebilir.
                Nüfus değerleri TÜİK mahalle verilerine dayanır. Listelenen mahalleler toplam{' '}
                {coveredPopulation.toLocaleString('tr-TR')} kişilik bir nüfusu kapsar.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold mb-3">Karaköprü’de en sık başvurulan hizmetler</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { to: '/kedi/kisirlastirma', title: 'Kedi kısırlaştırma', desc: 'Operasyon süreci, iyileşme ve fiyatı etkileyen faktörler' },
                  { to: '/kopek/kisirlastirma', title: 'Köpek kısırlaştırma', desc: 'Yaş, cinsiyet ve ırka göre değişen operasyon planı' },
                  { to: '/kedi/asi-takvimi', title: 'Kedi aşı takvimi', desc: 'Yavru ve erişkin kedilerde aşı sırası' },
                  { to: '/kopek/asi-takvimi', title: 'Köpek aşı takvimi', desc: 'Karma, kuduz ve bronşin aşıları' },
                  { to: '/acil-veteriner', title: '7/24 acil veteriner', desc: 'Gece ve tatil günlerinde acil müdahale' },
                  { to: '/rontgen-fiyatlari', title: 'Röntgen ve görüntüleme', desc: 'Dijital röntgen ve ultrason' }
                ].map(s => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="group p-4 rounded-xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <Stethoscope className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold text-gray-900 group-hover:text-primary-700">{s.title}</div>
                        <div className="text-sm text-gray-600 mt-1">{s.desc}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </article>

          {/* Yan sütun */}
          <aside className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-3">Klinik bilgileri</h3>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500">Adres</dt>
                  <dd className="text-gray-800">{contactInfo.address}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Hafta içi</dt>
                  <dd className="text-gray-800">{contactInfo.workingHours.weekdays}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Hafta sonu</dt>
                  <dd className="text-gray-800">{contactInfo.workingHours.weekend}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Acil</dt>
                  <dd className="text-gray-800 font-medium">{contactInfo.workingHours.emergency}</dd>
                </div>
              </dl>
              <a href={telHref} className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" /> Hemen ara
              </a>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-red-900">Acil durumda</h3>
              </div>
              <p className="text-sm text-red-800 leading-relaxed">
                Zehirlenme, travma, doğum güçlüğü, nefes darlığı ve idrar yapamama vakalarında
                beklemeyin. Yola çıkmadan önce arayın, biz hazırlanalım.
              </p>
              <Link to="/acil-veteriner" className="text-sm font-semibold text-red-700 hover:underline mt-3 inline-block">
                Acil veteriner sayfası →
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  )
}

export default KarakopruHub
